import { useCallback, useEffect, useState } from "react";
import {
  ChatMessage,
  ChatSubscribeMessage,
  MessageReadPayload,
  SendMessagePayload,
  TypingPayload,
  TypingResponse,
} from "types/socket";

import useChatSocket from "./useChatSocket";

export function useConversationSocket(conversationId: string) {
  const { socket } = useChatSocket();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isPatientTyping, setPatientIsTyping] = useState<boolean>(false);

  // Join conversation room + listen events
  useEffect(() => {
    if (!socket || !conversationId) return;

    const joinConversationRoom = () => {
      socket.emit(ChatSubscribeMessage.JOIN_CONVERSATION, { conversationId });
    };

    // ensure we re-join after the socket reconnects
    socket.on("connect", joinConversationRoom);
    joinConversationRoom();

    const onNewMessage = (message: ChatMessage) => {
      if (message.conversationId !== conversationId) return;
      setMessages((prev) => [...prev, message]);
      setPatientIsTyping(false);
    };

    const onTyping = (typing: TypingResponse) => {
      if (typing.conversationId !== conversationId) return;
      setPatientIsTyping(true);
    };

    const onMessageRead = (message: MessageReadPayload) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === message.messageId
            ? { ...msg, readAt: message.readAt }
            : msg
        )
      );
    };

    socket.on(ChatSubscribeMessage.NEW_MESSAGE, onNewMessage);
    socket.on(ChatSubscribeMessage.USER_TYPING, onTyping);
    socket.on(ChatSubscribeMessage.MESSAGE_READ, onMessageRead);
    return () => {
      socket.off("connect", joinConversationRoom);
      socket.off(ChatSubscribeMessage.NEW_MESSAGE, onNewMessage);
      socket.off(ChatSubscribeMessage.USER_TYPING, onTyping);
      socket.off(ChatSubscribeMessage.MESSAGE_READ, onMessageRead);
    };
  }, [socket, conversationId]);

  // Send message (text or with attachments)
  const sendMessage = useCallback(
    (options: SendMessagePayload) => {
      if (!socket) return;
      socket.emit(ChatSubscribeMessage.SEND_MESSAGE, options);
    },
    [socket]
  );

  // send typing event
  const sendTyping = useCallback(
    (options: TypingPayload) => {
      if (!socket) return;
      socket.emit(ChatSubscribeMessage.TYPING, options);
    },
    [socket]
  );

  // mark message as read
  const markMessageAsRead = useCallback(
    (messageId: string) => {
      if (!socket) return;
      socket.emit(ChatSubscribeMessage.MESSAGE_READ, { messageId });
    },
    [socket]
  );

  // Clear messages when conversation changes
  useEffect(() => {
    if (!conversationId) return;
    setMessages([]);
    setPatientIsTyping(false);
  }, [conversationId]);

  useEffect(() => {
    if (!isPatientTyping) return;
    const timeout = setTimeout(() => {
      setPatientIsTyping(false);
    }, 4000);
    return () => clearTimeout(timeout);
  }, [isPatientTyping]);

  return {
    messages,
    isPatientTyping,
    setMessages,
    sendMessage,
    sendTyping,
    markMessageAsRead,
  };
}
